/**
 * Cognito OIDC Auth Service
 *
 * Handles the authorization code flow with PKCE:
 * 1. Redirect to Cognito Hosted UI → Federate → Midway
 * 2. Handle callback with authorization code
 * 3. Exchange code for tokens
 * 4. Store tokens and provide access token for API calls
 * 5. Extract user info from ID token
 */

// Auth configuration per environment
interface AuthConfig {
  cognitoDomain: string;
  clientId: string;
  redirectUri: string;
  scopes: string;
  userPoolId: string;
}

const ENV_CONFIGS: Record<string, AuthConfig> = {
  'beta.ordis.fpa.amazon.dev': {
    cognitoDomain: 'https://gft-ordis-beta.auth.us-west-2.amazoncognito.com',
    clientId: '2jlkki1pdguvd0tjntcq17spmb',
    redirectUri: 'https://beta.ordis.fpa.amazon.dev/',
    scopes: 'openid email profile',
    userPoolId: 'us-west-2_AqsHvLWa6',
  },
  'gamma.ordis.fpa.amazon.dev': {
    cognitoDomain: 'https://gft-ordis-gamma.auth.us-west-2.amazoncognito.com',
    clientId: '57hmar3sp3fei8p39vea8g54bd',
    redirectUri: 'https://gamma.ordis.fpa.amazon.dev/',
    scopes: 'openid email profile',
    userPoolId: 'us-west-2_qj7gf8fx8',
  },
  // Prod uses the ROOT domain (no stage subdomain). Without this entry
  // getAuthConfig() falls back to the Beta config, so Prod redirected users
  // to beta.ordis.fpa.amazon.dev on login/bootstrap. Values from
  // GFTOrdisServiceCDK PROD_STAGE; the cognito domain is confirmed against
  // the deployed ALB (gft-ordis-prod.auth...).
  'ordis.fpa.amazon.dev': {
    cognitoDomain: 'https://gft-ordis-prod.auth.us-west-2.amazoncognito.com',
    clientId: '45vgh9glaogb5loq87lanr72fj',
    redirectUri: 'https://ordis.fpa.amazon.dev/',
    scopes: 'openid email profile',
    userPoolId: 'us-west-2_T4BycbfRh',
  },
};

/** Select auth config based on hostname, fallback to Beta */
export function getAuthConfig(): AuthConfig {
  const hostname = window.location.hostname;
  return ENV_CONFIGS[hostname] || ENV_CONFIGS['beta.ordis.fpa.amazon.dev'];
}

const AUTH_CONFIG = getAuthConfig();

interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

interface UserInfo {
  alias: string;
  email: string;
  givenName: string;
  familyName: string;
}

// Generate random string for PKCE
function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(36).padStart(2, '0'))
    .join('')
    .substring(0, length);
}

// SHA-256 hash for PKCE
async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  return crypto.subtle.digest('SHA-256', encoder.encode(plain));
}

// Base64 URL encode for PKCE
function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

class AuthService {
  private accessToken: string | null = null;
  private idToken: string | null = null;
  private refreshToken: string | null = null;
  private tokenExpiry: number = 0;
  private userInfo: UserInfo | null = null;

  constructor() {
    // Try to restore tokens from sessionStorage
    this.accessToken = sessionStorage.getItem('ordis_access_token');
    this.idToken = sessionStorage.getItem('ordis_id_token');
    this.refreshToken = sessionStorage.getItem('ordis_refresh_token');
    this.tokenExpiry = parseInt(sessionStorage.getItem('ordis_token_expiry') || '0', 10);

    if (this.idToken) {
      this.userInfo = this.parseIdToken(this.idToken);
    }
  }

  /** Check if user is authenticated with a valid token */
  isAuthenticated(): boolean {
    return !!this.accessToken && Date.now() < this.tokenExpiry;
  }

  /**
   * ALB session bootstrap.
   *
   * The backend authenticates every request via the ALB's
   * `authenticateCognito` action, which injects a signed
   * `x-amzn-oidc-data` header the Query Resolver verifies. That requires
   * the browser to hold the `AWSELBAuthSessionCookie`. But the SPA is
   * served from S3/CloudFront (bypassing the ALB) and talks to the
   * backend only via background `fetch('/query')`, which cannot follow
   * the cross-origin 302 the ALB issues to the Cognito Hosted UI — so
   * the cookie never gets minted and `/query` fails with a network
   * error.
   *
   * `bootstrapAlbSession()` fixes that by doing a full-page navigation to
   * `/auth/bootstrap` — an ALB listener rule that runs the Cognito flow
   * (silent when a Cognito session already exists), mints the session
   * cookie, then 302s back to `/`. We record the time we last bootstrapped
   * in localStorage (shared across tabs) and treat the session as fresh
   * for 11h — comfortably under the ALB's 12h `sessionTimeout` — to avoid
   * a redundant redirect on every load while staying ahead of expiry.
   */
  isAlbSessionFresh(): boolean {
    const ts = parseInt(localStorage.getItem('ordis_alb_bootstrap_ts') || '0', 10);
    return ts > 0 && Date.now() - ts < 11 * 60 * 60 * 1000;
  }

  /** Full-page navigation through the ALB Cognito flow to mint the session cookie. */
  bootstrapAlbSession(): void {
    // Record the attempt up front. If the cookie fails to mint, the
    // reactive path in ordisApiService still surfaces the error rather
    // than looping, because it only re-bootstraps when the session is
    // NOT fresh.
    localStorage.setItem('ordis_alb_bootstrap_ts', Date.now().toString());
    window.location.href = '/auth/bootstrap';
  }

  /** Drop the ALB-session freshness marker (forces a re-bootstrap). */
  invalidateAlbSession(): void {
    localStorage.removeItem('ordis_alb_bootstrap_ts');
  }

  /** Get the current access token for API calls */
  getAccessToken(): string | null {
    if (!this.isAuthenticated()) return null;
    return this.accessToken;
  }

  /** Get user info extracted from ID token */
  getUserInfo(): UserInfo | null {
    return this.userInfo;
  }

  /** Get user alias */
  getUserAlias(): string | null {
    return this.userInfo?.alias || null;
  }

  /** Start the login flow — redirect to Cognito Hosted UI */
  async login(): Promise<void> {
    // Generate PKCE code verifier and challenge
    const codeVerifier = generateRandomString(64);
    const challengeBuffer = await sha256(codeVerifier);
    const codeChallenge = base64UrlEncode(challengeBuffer);

    // Store code verifier for the callback
    sessionStorage.setItem('ordis_code_verifier', codeVerifier);

    // Build the authorization URL
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: AUTH_CONFIG.clientId,
      redirect_uri: AUTH_CONFIG.redirectUri,
      scope: AUTH_CONFIG.scopes,
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
    });

    window.location.href = `${AUTH_CONFIG.cognitoDomain}/oauth2/authorize?${params.toString()}`;
  }

  /** Handle the callback after Cognito redirects back with an authorization code */
  async handleCallback(): Promise<boolean> {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (!code) return false;

    const codeVerifier = sessionStorage.getItem('ordis_code_verifier');
    if (!codeVerifier) {
      console.error('No code verifier found');
      return false;
    }

    try {
      // Exchange authorization code for tokens
      const tokenResponse = await fetch(`${AUTH_CONFIG.cognitoDomain}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: AUTH_CONFIG.clientId,
          code: code,
          redirect_uri: AUTH_CONFIG.redirectUri,
          code_verifier: codeVerifier,
        }),
      });

      if (!tokenResponse.ok) {
        console.error('Token exchange failed:', await tokenResponse.text());
        return false;
      }

      const tokens: TokenResponse = await tokenResponse.json();
      this.storeTokens(tokens);

      // Clean up URL (remove code parameter)
      window.history.replaceState({}, document.title, window.location.pathname);
      sessionStorage.removeItem('ordis_code_verifier');

      return true;
    } catch (error) {
      console.error('Auth callback error:', error);
      return false;
    }
  }

  /** Logout — clear tokens and redirect to Cognito logout */
  logout(): void {
    sessionStorage.removeItem('ordis_access_token');
    sessionStorage.removeItem('ordis_id_token');
    sessionStorage.removeItem('ordis_refresh_token');
    sessionStorage.removeItem('ordis_token_expiry');
    this.accessToken = null;
    this.idToken = null;
    this.refreshToken = null;
    this.tokenExpiry = 0;
    this.userInfo = null;

    const params = new URLSearchParams({
      client_id: AUTH_CONFIG.clientId,
      logout_uri: AUTH_CONFIG.redirectUri,
    });
    window.location.href = `${AUTH_CONFIG.cognitoDomain}/logout?${params.toString()}`;
  }

  private storeTokens(tokens: TokenResponse): void {
    this.accessToken = tokens.access_token;
    this.idToken = tokens.id_token;
    this.refreshToken = tokens.refresh_token || null;
    this.tokenExpiry = Date.now() + tokens.expires_in * 1000;

    sessionStorage.setItem('ordis_access_token', this.accessToken);
    sessionStorage.setItem('ordis_id_token', this.idToken);
    if (this.refreshToken) sessionStorage.setItem('ordis_refresh_token', this.refreshToken);
    sessionStorage.setItem('ordis_token_expiry', this.tokenExpiry.toString());

    this.userInfo = this.parseIdToken(this.idToken);
  }

  private parseIdToken(idToken: string): UserInfo | null {
    try {
      const payload = idToken.split('.')[1];
      const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
      const decoded = JSON.parse(atob(padded));

      // Extract alias from identities (Federate userId) or username
      let alias = '';
      if (decoded.identities) {
        try {
          const identities =
            typeof decoded.identities === 'string' ? JSON.parse(decoded.identities) : decoded.identities;
          if (identities.length > 0) alias = identities[0].userId || '';
        } catch {
          /* ignore parse errors */
        }
      }
      if (!alias && decoded['cognito:username']) {
        const username = decoded['cognito:username'];
        alias = username.includes('_') ? username.split('_').slice(1).join('_') : username;
      }

      return {
        alias: alias || decoded.sub,
        email: decoded.email || '',
        givenName: decoded.given_name || '',
        familyName: decoded.family_name || '',
      };
    } catch (error) {
      console.error('Failed to parse ID token:', error);
      return null;
    }
  }
}

export const authService = new AuthService();
